// v186 admin scheduled-banner page.
//
// Three sections, top-to-bottom:
//   * "New banner" form (title, body, severity, schedule window,
//     dismissible toggle).
//   * "Active right now" — at most one banner. Shown big with a
//     preview that mirrors what AppShell renders.
//   * "Scheduled" + "Past" — collapsed lists with delete buttons.
//
// Banner severity colors:
//   info     → accent (blue)
//   warning  → warning (amber)
//   critical → danger (red)
// These match the AppShell SystemBanner component so the preview
// looks like the real thing.

import { useMemo, useState } from 'react';
import { Megaphone, Plus, Trash2, AlertTriangle, Info } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useBanners,
  createBanner,
  deleteBanner,
  bannerLifecycle,
  type Banner,
  type BannerSeverity,
} from '../../api/banners';

const SEVERITY_TONES: Record<BannerSeverity, 'accent' | 'warning' | 'danger'> = {
  info: 'accent',
  warning: 'warning',
  critical: 'danger',
};

function bannerSeverityIcon(severity: BannerSeverity) {
  if (severity === 'info') return Info;
  if (severity === 'warning') return Megaphone;
  return AlertTriangle;
}

// Static map so Tailwind's JIT picks up these class names. Avoid
// computing them via string concatenation (`'text-' + tone`) which
// would silently strip the colour from the bundle.
const ICON_TEXT_CLASS: Record<BannerSeverity, string> = {
  info: 'text-accent',
  warning: 'text-warning',
  critical: 'text-danger',
};

// Format a Date as the value an <input type="datetime-local"> wants:
// 'YYYY-MM-DDTHH:mm' in the browser's local zone. Used to pre-fill
// the schedule inputs with a sensible default (now → +1 hour).
function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    d.getFullYear() +
    '-' + pad(d.getMonth() + 1) +
    '-' + pad(d.getDate()) +
    'T' + pad(d.getHours()) +
    ':' + pad(d.getMinutes())
  );
}

// datetime-local inputs emit a string with no offset (eg
// '2026-04-30T18:00'). We treat that as the operator's local time and
// convert to RFC3339 UTC for the API. new Date(localStr) does that
// natively in modern browsers.
function localInputToRFC3339(s: string): string {
  return new Date(s).toISOString();
}

function fmtTime(rfc: string): string {
  try {
    return new Date(rfc).toLocaleString();
  } catch {
    return rfc;
  }
}

export function AdminBanners() {
  const { data, mutate } = useBanners();

  const now = new Date();
  const defaultStart = useMemo(() => toLocalInput(now), []); // eslint-disable-line react-hooks/exhaustive-deps
  const defaultEnd = useMemo(
    () => toLocalInput(new Date(Date.now() + 60 * 60 * 1000)),
    [],
  );

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<BannerSeverity>('info');
  const [startsAt, setStartsAt] = useState(defaultStart);
  const [endsAt, setEndsAt] = useState(defaultEnd);
  const [dismissible, setDismissible] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const banners: Banner[] = data?.banners ?? [];
  const active = banners.find((b) => bannerLifecycle(b) === 'active');
  const scheduled = banners.filter((b) => bannerLifecycle(b) === 'scheduled');
  const past = banners.filter((b) => bannerLifecycle(b) === 'past').slice(0, 25);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await createBanner({
        title: title.trim(),
        body: body.trim() || undefined,
        severity,
        starts_at: localInputToRFC3339(startsAt),
        ends_at: localInputToRFC3339(endsAt),
        dismissible,
      });
      setTitle('');
      setBody('');
      setSeverity('info');
      setDismissible(true);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Delete this banner? Active banners disappear immediately.')) return;
    try {
      await deleteBanner(id);
      await mutate();
    } catch (e) {
      setErr(String(e));
    }
  };

  return (
    <AdminShell
      title="Banners"
      description="Schedule a customer-facing announcement banner that auto-appears at start time and auto-removes at end time. Useful for maintenance windows, EOL notices, and post-incident follow-ups."
    >
      <Card>
        <CardHeader title="New banner" />
        <CardBody>
          <form onSubmit={onCreate} className="space-y-4">
            <div>
              <label htmlFor="banner-title" className="text-sm text-fg-muted block mb-1.5">Title</label>
              <Input
                id="banner-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Maintenance window — Saturday 02:00–04:00 UTC"
                maxLength={200}
                required
              />
            </div>

            <div>
              <label htmlFor="banner-body" className="text-sm text-fg-muted block mb-1.5">
                Body <span className="text-fg-subtle">(optional, plain text)</span>
              </label>
              <textarea
                id="banner-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Detection scans will pause during the window. Uploads and downloads remain available."
                maxLength={2000}
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg placeholder:text-fg-subtle border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm font-mono"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="banner-severity" className="text-sm text-fg-muted block mb-1.5">Severity</label>
                <select
                  id="banner-severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as BannerSeverity)}
                  className="h-10 w-full px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm"
                >
                  <option value="info">info — blue</option>
                  <option value="warning">warning — amber</option>
                  <option value="critical">critical — red</option>
                </select>
              </div>
              <div>
                <label htmlFor="banner-starts-at" className="text-sm text-fg-muted block mb-1.5">Starts (your time)</label>
                <Input
                  id="banner-starts-at"
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="banner-ends-at" className="text-sm text-fg-muted block mb-1.5">Ends (your time)</label>
                <Input
                  id="banner-ends-at"
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  required
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={dismissible}
                onChange={(e) => setDismissible(e.target.checked)}
              />
              <span>Allow users to dismiss the banner</span>
            </label>

            <div className="flex items-center gap-3">
              <Button variant="primary" type="submit" disabled={busy || !title.trim()}>
                <Plus className="h-4 w-4" />
                {busy ? 'Scheduling…' : 'Schedule banner'}
              </Button>
              {err && <span className="text-xs text-danger font-mono">{err}</span>}
            </div>
          </form>
        </CardBody>
      </Card>

      <Card className="mt-6">
        <CardHeader
          title="Active right now"
          description="At most one banner is shown to users at any moment — the most recently scheduled row whose window contains 'now'."
        />
        <CardBody>
          {active ? (
            <BannerRow b={active} onDelete={onDelete} />
          ) : (
            <p className="text-sm text-fg-muted">No banner is currently active.</p>
          )}
        </CardBody>
      </Card>

      {scheduled.length > 0 && (
        <Card className="mt-6">
          <CardHeader title={`Scheduled (${scheduled.length})`} />
          <CardBody className="space-y-3">
            {scheduled.map((b) => (
              <BannerRow key={b.id} b={b} onDelete={onDelete} />
            ))}
          </CardBody>
        </Card>
      )}

      {past.length > 0 && (
        <Card className="mt-6">
          <CardHeader title="Past" description="Most recent 25 expired banners." />
          <CardBody className="space-y-3">
            {past.map((b) => (
              <BannerRow key={b.id} b={b} onDelete={onDelete} muted />
            ))}
          </CardBody>
        </Card>
      )}
    </AdminShell>
  );
}

function BannerRow({
  b,
  onDelete,
  muted = false,
}: Readonly<{
  b: Banner;
  onDelete: (id: string) => void;
  muted?: boolean;
}>) {
  const tone = SEVERITY_TONES[b.severity];
  const Icon = bannerSeverityIcon(b.severity);
  return (
    <div
      className={
        'flex items-start gap-3 rounded-md border px-4 py-3 ' +
        (muted
          ? 'border-border bg-bg-inset/40 opacity-70'
          : 'border-border bg-bg-inset/60')
      }
    >
      <Icon className={'h-4 w-4 mt-0.5 shrink-0 ' + ICON_TEXT_CLASS[b.severity]} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-fg">{b.title}</span>
          <Badge tone={tone}>{b.severity}</Badge>
          {!b.dismissible && <Badge tone="neutral">non-dismissible</Badge>}
        </div>
        {b.body && (
          <p className="text-sm text-fg-muted mt-1 whitespace-pre-wrap break-words">
            {b.body}
          </p>
        )}
        <p className="text-xs text-fg-subtle mt-2">
          {fmtTime(b.starts_at)} → {fmtTime(b.ends_at)}
          {b.created_by ? ' · by ' + b.created_by : ''}
        </p>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onDelete(b.id)} aria-label="Delete banner">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
